export function normalizeTimestamp(value) {
  if (value == null) return null;
  if (typeof value?.toDate === "function") {
    const next = value.toDate();
    return Number.isNaN(next?.getTime?.()) ? null : next;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === "number") {
    const next = new Date(value);
    return Number.isNaN(next.getTime()) ? null : next;
  }
  if (typeof value === "string") {
    const next = new Date(value);
    return Number.isNaN(next.getTime()) ? null : next;
  }
  return null;
}

export function isValidAt(doc, asOfDate) {
  if (!doc) return false;
  const at = normalizeTimestamp(asOfDate);
  if (!at) return true;
  const from = normalizeTimestamp(doc.validFrom);
  const to = normalizeTimestamp(doc.validTo);
  const fromOk = !from || from.getTime() <= at.getTime();
  const toOk = !to || at.getTime() < to.getTime();
  return fromOk && toOk;
}

export function isValidInRange(doc, start, end) {
  if (!doc) return false;
  const startDate = normalizeTimestamp(start);
  const endDate = normalizeTimestamp(end);
  const from = normalizeTimestamp(doc.validFrom);
  const to = normalizeTimestamp(doc.validTo);

  // If no range bounds are supplied, treat as visible.
  if (!startDate && !endDate) return true;

  // Treat missing bounds as open interval.
  const docStart = from ? from.getTime() : Number.NEGATIVE_INFINITY;
  const docEnd = to ? to.getTime() : Number.POSITIVE_INFINITY;
  const rangeStart = startDate ? startDate.getTime() : Number.NEGATIVE_INFINITY;
  const rangeEnd = endDate ? endDate.getTime() : Number.POSITIVE_INFINITY;

  // Overlap test for half-open intervals: [docStart, docEnd) intersects [rangeStart, rangeEnd)
  return docStart < rangeEnd && rangeStart < docEnd;
}

export function isOccurredInRange(doc, start, end) {
  if (!doc) return false;
  const occurredAt = normalizeTimestamp(doc.occurredAt);
  if (!occurredAt) return false;
  const startDate = normalizeTimestamp(start);
  const endDate = normalizeTimestamp(end);
  if (startDate && occurredAt.getTime() < startDate.getTime()) return false;
  if (endDate && occurredAt.getTime() >= endDate.getTime()) return false;
  return true;
}

export function toDateTimeLocalValue(value) {
  const date = normalizeTimestamp(value);
  if (!date) return "";
  const shifted = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return shifted.toISOString().slice(0, 16);
}

export function fromDateTimeLocalValue(raw) {
  const value = String(raw || "").trim();
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

function getTimeZoneOffsetMillis(date, timeZone) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(date);

  const map = {};
  parts.forEach((part) => {
    if (part.type !== "literal") map[part.type] = part.value;
  });

  const asUTC = Date.UTC(
    Number(map.year),
    Number(map.month) - 1,
    Number(map.day),
    Number(map.hour),
    Number(map.minute),
    Number(map.second)
  );
  return asUTC - date.getTime();
}

export function toDateTimeLocalValueInTimeZone(value, timeZone) {
  const date = normalizeTimestamp(value);
  if (!date) return "";
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(date);

  const map = {};
  parts.forEach((part) => {
    if (part.type !== "literal") map[part.type] = part.value;
  });
  return `${map.year}-${map.month}-${map.day}T${map.hour}:${map.minute}`;
}

export function fromDateTimeLocalValueInTimeZone(raw, timeZone) {
  const value = String(raw || "").trim();
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);

  const utcGuess = new Date(Date.UTC(year, month - 1, day, hour, minute, 0));
  if (Number.isNaN(utcGuess.getTime())) return null;
  const offset = getTimeZoneOffsetMillis(utcGuess, timeZone);
  return new Date(utcGuess.getTime() - offset);
}
